// ──────────────────────────────────────────────
// Components/AIBot/index.js
// Floating AI assistant: a half hidden launcher bubble that slides out on hover,
// and a texting style chat window at the bottom right. The header holds the lab picker.
// Chat state and API calls live in Hooks/useAIBot.
// ──────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useAIBot from "../../Hooks/useAIBot";
import "./styles.css";

// ─── Constants ───

const BOT_NAME = "ASTRO";

// Also part of the aria label, because touch screens have no hover tooltip.
const LOCKED_HINT = "Start a new chat to switch labs";

// ─── Icons ───

const Icon = ({ size = 20, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

const BotIcon = ({ size }) => (
  <Icon size={size}>
    <rect x="4" y="8" width="16" height="11" rx="3" />
    <path d="M12 8V5" />
    <circle cx="12" cy="4" r="1" />
    <path d="M2 12v3M22 12v3" />
    <path d="M9 13h.01M15 13h.01" />
    <path d="M10 16.5h4" />
  </Icon>
);

const SendIcon = () => (
  <Icon size={18}>
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </Icon>
);

const MinimizeIcon = () => (
  <Icon>
    <polyline points="6 9 12 15 18 9" />
  </Icon>
);

const RestartIcon = () => (
  <Icon size={18}>
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </Icon>
);

const ChevronIcon = () => (
  <Icon size={14}>
    <polyline points="6 9 12 15 18 9" />
  </Icon>
);

const LockIcon = () => (
  <Icon size={14}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </Icon>
);

// ─── Component ───

export default function AIBot() {
  const [isOpen, setIsOpen] = useState(false),
    [draft, setDraft] = useState(""),
    {
      messages,
      isLoading,
      error,
      lab,
      labs,
      isLabLocked,
      selectLab,
      sendMessage,
      retry,
      clearChat,
    } = useAIBot(),
    launcherRef = useRef(null),
    labRef = useRef(null),
    inputRef = useRef(null),
    listRef = useRef(null),
    wasOpenRef = useRef(false);

  // ─── Effects ───

  // Focus moves into the window on open (picker if no lab yet, else the input) and back
  // to the launcher on close. Only open and close count, so picking a lab never steals focus.
  useEffect(() => {
    if (isOpen === wasOpenRef.current) return;
    if (isOpen) {
      (lab ? inputRef : labRef).current?.focus();
    } else {
      launcherRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, lab]);

  // scrollTop instead of scrollIntoView, so the page behind the widget does not jump.
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, isLoading, error, isOpen]);

  // Grow the input with its text, up to the max height in the stylesheet.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${input.scrollHeight + input.offsetHeight - input.clientHeight}px`;
  }, [draft, isOpen]);

  // ─── Handlers ───

  const closeChat = () => setIsOpen(false);

  const submitDraft = () => {
    if (!lab || !draft.trim() || isLoading) return;
    sendMessage(draft);
    setDraft("");
  };

  const handleInputKeyDown = (e) => {
    // isComposing: IME users press Enter to confirm a character, that must not send.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submitDraft();
    }
  };

  const handleWindowKeyDown = (e) => {
    if (e.key === "Escape") closeChat();
  };

  // ─── Render ───

  // Portaled to body so no page layout transform or overflow can break the fixed position.
  return createPortal(
    <div className="aibot">
      <div
        className={`aibot-launcher${isOpen ? " aibot-launcher--hidden" : ""}`}
      >
        <button
          type="button"
          ref={launcherRef}
          className="aibot-launcher__disc"
          aria-label={`Open chat with ${BOT_NAME}`}
          onClick={() => setIsOpen(true)}
        >
          <BotIcon size={30} />
        </button>
      </div>

      {isOpen && (
        <div
          className="aibot-window"
          role="dialog"
          aria-label={`Chat with ${BOT_NAME}`}
          onKeyDown={handleWindowKeyDown}
        >
          <div className="aibot-header">
            <span className="aibot-header__avatar">
              <BotIcon size={22} />
            </span>
            <div className="aibot-header__text">
              <div className="aibot-header__title">{BOT_NAME}</div>
              <label
                className="aibot-lab"
                title={isLabLocked ? LOCKED_HINT : undefined}
              >
                <span className="aibot-lab__text">Lab</span>
                <span className="aibot-lab__field">
                  <select
                    ref={labRef}
                    className="aibot-lab__select"
                    aria-label={
                      isLabLocked ? `Lab, ${LOCKED_HINT.toLowerCase()}` : "Lab"
                    }
                    value={lab}
                    disabled={isLabLocked}
                    onChange={(e) => selectLab(e.target.value)}
                  >
                    {/* Only offered until a lab is picked, nobody can go back to having none */}
                    {!lab && (
                      <option value="" disabled>
                        Select a lab
                      </option>
                    )}
                    {labs.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                  <span className="aibot-lab__icon">
                    {isLabLocked ? <LockIcon /> : <ChevronIcon />}
                  </span>
                </span>
              </label>
            </div>
            <button
              type="button"
              className="aibot-icon-btn"
              title="New chat"
              aria-label="Start a new chat"
              onClick={clearChat}
            >
              <RestartIcon />
            </button>
            <button
              type="button"
              className="aibot-icon-btn"
              title="Minimize"
              aria-label="Minimize chat"
              onClick={closeChat}
            >
              <MinimizeIcon />
            </button>
          </div>

          <div
            className="aibot-messages"
            ref={listRef}
            role="log"
            aria-live="polite"
          >
            {messages.map((msg) => (
              // Plain text on purpose, so a reply can never inject markup.
              // PLACEHOLDER: swap in a markdown renderer if the bot starts replying with lists or code.
              <div key={msg.id} className={`aibot-msg aibot-msg--${msg.role}`}>
                {msg.text}
              </div>
            ))}

            {isLoading && (
              <div
                className="aibot-msg aibot-msg--bot"
                role="status"
                aria-label={`${BOT_NAME} is typing`}
              >
                <span className="aibot-typing">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            )}

            {error && (
              <div className="aibot-error" role="alert">
                <span>{error}</span>
                <button type="button" onClick={retry}>
                  Retry
                </button>
              </div>
            )}
          </div>

          <form
            className="aibot-form"
            onSubmit={(e) => {
              e.preventDefault();
              submitDraft();
            }}
          >
            <textarea
              ref={inputRef}
              className="aibot-input"
              rows={1}
              value={draft}
              disabled={!lab}
              placeholder={lab ? "Type a message..." : "Pick a lab first"}
              aria-label="Message"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleInputKeyDown}
            />
            <button
              type="submit"
              className="aibot-send"
              aria-label="Send message"
              disabled={!lab || !draft.trim() || isLoading}
            >
              <SendIcon />
            </button>
          </form>
        </div>
      )}
    </div>,
    document.body,
  );
}
